'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/nextjs';
import { useRouter } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { ClaimProfileDialog } from './ClaimProfileDialog';

interface ClaimProfileButtonProps {
  providerId: string;
  companyName: string;
  claimStatus: 'unclaimed' | 'pending' | 'claimed';
  autoOpen?: boolean;
}

export const ClaimProfileButton = ({
  providerId,
  companyName,
  claimStatus,
  autoOpen = false,
}: ClaimProfileButtonProps) => {
  const { isLoaded, isSignedIn } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const router = useRouter();
  const canClaim = claimStatus === 'unclaimed';
  const autoOpenedFor = useRef<string | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const claimUrl = `/company/onboarding/?providerId=${encodeURIComponent(providerId)}`;

  const handleClaim = useCallback(() => {
    if (!isLoaded || !canClaim) {
      return;
    }

    if (isSignedIn) {
      router.push(claimUrl);
      return;
    }

    setDialogOpen(true);
  }, [isLoaded, canClaim, isSignedIn, router, claimUrl]);

  useEffect(() => {
    if (!autoOpen || !canClaim || !isLoaded || autoOpenedFor.current === providerId) {
      return;
    }

    autoOpenedFor.current = providerId;
    handleClaim();
  }, [autoOpen, canClaim, isLoaded, providerId, handleClaim]);

  if (claimStatus === 'claimed') {
    return null;
  }

  if (claimStatus === 'pending') {
    return (
      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
        <span>Claim pending</span>
      </Badge>
    );
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={handleClaim}
        disabled={!isLoaded}
        className="inline-flex min-h-8 cursor-pointer items-center whitespace-nowrap rounded-sm text-left text-sm text-slate-600 underline decoration-slate-300 underline-offset-4 transition-colors hover:text-slate-900 hover:decoration-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-100 disabled:cursor-wait disabled:opacity-50"
      >
        <span>Claim this profile</span>
      </button>
      <ClaimProfileDialog key={providerId} claimUrl={claimUrl} companyName={companyName} open={dialogOpen} onOpenChange={setDialogOpen} restoreFocus={() => { trigger.current?.focus(); }} />
    </>
  );
};
